import { useState } from "react";
import type { PieChartModel } from "@/types/dashboard";

const DEFAULT_SEGMENT_NAME = "Unknown";
const DEFAULT_SEGMENT_COLOR = "#c1c1c1";

const ENCODED_CHAR_MAP: Record<string, string> = {
  "&": " and ",
  "<": "",
  ">": "",
  '"': "",
  "'": "",
  "`": ""
};

function neutralizeMarkupChars(value: string): string {
  return value.replace(/[&<>"'`]/g, (char) => ENCODED_CHAR_MAP[char] ?? "");
}

function sanitizeText(value: unknown, fallback = ""): string {
  if (typeof value !== "string") return fallback;
  const withoutControlChars = value.replace(/[\u0000-\u001F\u007F]/g, "");
  const cleaned = neutralizeMarkupChars(withoutControlChars).trim();
  return cleaned || fallback;
}

function sanitizeNumber(value: unknown): number {
  if (typeof value !== "number") return 0;
  if (!Number.isFinite(value) || value < 0) return 0;
  return value;
}

function sanitizeColor(value: unknown): string {
  if (typeof value !== "string") return DEFAULT_SEGMENT_COLOR;
  const color = value.trim();
  const isHex = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(color);
  const isRgb = /^rgba?\(([^)]+)\)$/.test(color);
  const isHsl = /^hsla?\(([^)]+)\)$/.test(color);

  return isHex || isRgb || isHsl ? color : DEFAULT_SEGMENT_COLOR;
}

function sanitizeTooltipItems(items: string[]): string[] {
  return items
    .map((item) => sanitizeText(item))
    .filter((item) => item.length > 0);
}

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function arcPath(
  cx: number,
  cy: number,
  r: number,
  startAngle: number,
  endAngle: number
): string {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y} Z`;
}

interface SvgPieChartProps {
  data: PieChartModel[];
  otherHoverItems?: string[];
  selectedBusiness?: string | null;
}

function formatOtherTooltip(items: string[]): string {
  if (items.length === 0) return "";

  // Parse items in format "Name | Value" and sort by value descending
  const parsed = items
    .map((item) => {
      const parts = item.split("|").map((p) => p.trim());
      if (parts.length === 2) {
        const name = sanitizeText(parts[0], DEFAULT_SEGMENT_NAME);
        const valueStr = sanitizeText(parts[1]);
        const numericValue = Number.parseInt(valueStr.replace(/,/g, ""), 10);
        const value = Number.isFinite(numericValue) ? numericValue : 0;
        return { name, value, original: `${name} | ${valueStr}` };
      }
      const normalized = sanitizeText(item, DEFAULT_SEGMENT_NAME);
      return { name: normalized, value: 0, original: normalized };
    })
    .sort((a, b) => b.value - a.value);

  return parsed.map((p) => p.original).join("\n");
}

export function SvgPieChart({
  data,
  otherHoverItems = [],
  selectedBusiness
}: SvgPieChartProps) {
  const [activeTooltip, setActiveTooltip] = useState<{
    text: string;
    x: number;
    y: number;
  } | null>(null);

  if (!data || data.length === 0) return null;

  const safeData = data
    .map((segment) => ({
      segmentName: sanitizeText(segment.segmentName, DEFAULT_SEGMENT_NAME),
      segmentValue: sanitizeNumber(segment.segmentValue),
      segmentValueString: sanitizeText(segment.segmentValueString, "0"),
      segmentColor: sanitizeColor(segment.segmentColor)
    }))
    .filter((segment) => segment.segmentValue > 0);

  if (safeData.length === 0) return null;

  const safeOtherHoverItems = sanitizeTooltipItems(otherHoverItems);
  const selectedBusinessName = selectedBusiness ? sanitizeText(selectedBusiness) : null;

  function showTooltipFromMouse(event: React.MouseEvent<SVGElement>, text: string) {
    const svgRect = event.currentTarget.ownerSVGElement?.getBoundingClientRect();
    if (!svgRect) return;

    setActiveTooltip({
      text,
      x: event.clientX - svgRect.left,
      y: event.clientY - svgRect.top,
    });
  }

  function showTooltipFromFocus(event: React.FocusEvent<SVGElement>, text: string) {
    const svgRect = event.currentTarget.ownerSVGElement?.getBoundingClientRect();
    const targetRect = event.currentTarget.getBoundingClientRect();
    if (!svgRect || !targetRect) return;

    setActiveTooltip({
      text,
      x: targetRect.left - svgRect.left + targetRect.width / 2,
      y: targetRect.top - svgRect.top,
    });
  }

  function hideTooltip() {
    setActiveTooltip(null);
  }

  const total = safeData.reduce((sum, d) => sum + d.segmentValue, 0);
  if (total === 0) return null;

  if (safeData.length === 1) {
    const segment = safeData[0];
    const isOther =
      segment.segmentName.toLowerCase() === "other" && !selectedBusinessName;
    const tooltipText = isOther
      ? formatOtherTooltip(safeOtherHoverItems)
      : `${segment.segmentName} | ${segment.segmentValueString}`;

    return (
      <div className="relative h-full w-full">
        <svg viewBox="0 0 200 200" role="img" aria-label="Pie chart" className="h-full w-full">
          <circle
            cx="100"
            cy="100"
            r="90"
            fill={segment.segmentColor}
            stroke="white"
            strokeWidth="1.5"
            tabIndex={0}
            onMouseEnter={(event) => showTooltipFromMouse(event, tooltipText)}
            onMouseMove={(event) => showTooltipFromMouse(event, tooltipText)}
            onMouseLeave={hideTooltip}
            onFocus={(event) => showTooltipFromFocus(event, tooltipText)}
            onBlur={hideTooltip}
            aria-label={tooltipText}
          />
        </svg>
        {activeTooltip && (
          <span
            role="tooltip"
            className="pointer-events-none absolute z-50 w-60 -translate-x-1/2 -translate-y-full bg-black px-3 py-2 text-white custom-tooltip-content"
            style={{ left: activeTooltip.x, top: activeTooltip.y, borderRadius: 0 }}
          >
            {activeTooltip.text.split("\n").map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </span>
        )}
      </div>
    );
  }

  let currentAngle = 0;

  return (
    <div className="relative h-full w-full">
      <svg viewBox="0 0 200 200" role="img" aria-label="Pie chart" className="h-full w-full">
        {safeData.map((segment, index) => {
          const sliceAngle = (segment.segmentValue / total) * 360;
          const path = arcPath(100, 100, 90, currentAngle, currentAngle + sliceAngle);
          currentAngle += sliceAngle;

          const isOther =
            segment.segmentName.toLowerCase() === "other" && !selectedBusinessName;
          const tooltipText = isOther
            ? formatOtherTooltip(safeOtherHoverItems)
            : `${segment.segmentName} | ${segment.segmentValueString}`;

          return (
            <path
              key={`${segment.segmentName}-${index}`}
              d={path}
              fill={segment.segmentColor}
              stroke="white"
              strokeWidth="1.5"
              className="transition-opacity hover:opacity-80"
              tabIndex={0}
              onMouseEnter={(event) => showTooltipFromMouse(event, tooltipText)}
              onMouseMove={(event) => showTooltipFromMouse(event, tooltipText)}
              onMouseLeave={hideTooltip}
              onFocus={(event) => showTooltipFromFocus(event, tooltipText)}
              onBlur={hideTooltip}
              aria-label={tooltipText}
            />
          );
        })}
      </svg>
      {activeTooltip && (
        <span
          role="tooltip"
          className="pointer-events-none absolute z-50 w-60 -translate-x-1/2 -translate-y-full bg-black px-3 py-2 text-white custom-tooltip-content"
          style={{ left: activeTooltip.x, top: activeTooltip.y, borderRadius: 0 }}
        >
          {activeTooltip.text.split("\n").map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </span>
      )}
    </div>
  );
}
